// ═══════════════════════════════════════════════════════════════════════════════
// Admin Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { ADMIN_ROLE, TASK_KIND, USER_ROLE } from '../constants/index.js';
import { emailSchema, passwordSchema, uuidSchema, positiveIntSchema, dateSchema } from './common.schema.js';
import { SUBSCRIPTION_UPGRADE_COPY_LOCALE_CODE_REGEX, SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH, SUBSCRIPTION_UPGRADE_COPY_MAX_LOCALES, SUBSCRIPTION_UPGRADE_COPY_MAX_TITLE_LENGTH, } from '../utils/subscriptionUpgradeCopy.js';
import { isSubscriptionPromoDatetime } from '../utils/subscriptionPromotions.js';
import { membershipPricingCatalogSettingSchema } from './membershipPricing.schema.js';
// ── Admin Login Input Schema ──
export const adminLoginInputSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
    twoFactorCode: z.string().length(6, 'Two-factor code must be 6 digits').optional(),
    breakGlass: z.boolean().optional(),
});
// ── Create Admin Input Schema ──
export const createAdminInputSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: passwordSchema,
    role: z.enum([ADMIN_ROLE.SUPER_ADMIN, ADMIN_ROLE.SUPPORT_ADMIN], {
        error: 'Role must be super_admin or support_admin',
    }),
})
    .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
// ── Update Admin Input Schema ──
export const updateAdminInputSchema = z.object({
    role: z.enum([ADMIN_ROLE.SUPER_ADMIN, ADMIN_ROLE.SUPPORT_ADMIN]).optional(),
    twoFactorEnabled: z.boolean().optional(),
    isActive: z.boolean().optional(),
});
export const adminResetPasswordBodySchema = z
    .object({
    operatorPassword: z.string().min(1, 'Operator password is required'),
    newPassword: passwordSchema,
    confirmNewPassword: passwordSchema,
})
    .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});
export const adminResendTwoFactorBodySchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
});
// ── Admin ID Param Schema ──
export const adminIdParamSchema = z.object({
    id: uuidSchema,
});
// ── Admin User Search Query Schema ──
export const adminUserSearchQuerySchema = z.object({
    email: z.string().optional(),
    role: z.enum([USER_ROLE.EMPLOYER, USER_ROLE.HELPER, 'all']).default('all'),
    isPaired: z.enum(['true', 'false', 'all']).default('all'),
    level: z.enum(['1', '2', '3', 'all']).default('all'),
    accountRemovalRequested: z.enum(['true', 'false', 'all']).default('all'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ── Admin Reset User Password (body for PUT /users/:id/reset-password) ──
export const adminResetUserPasswordBodySchema = z
    .object({
    newPassword: passwordSchema,
    confirmNewPassword: passwordSchema,
})
    .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});
/** Admin manual employer level change (§22.4 / §22.7). */
export const adminUpdateEmployerLevelBodySchema = z.object({
    level: z.union([z.literal(2), z.literal(3)]),
});
/** Admin manual grant L3 trial until a fixed UTC datetime (§22.7 / §22.8). */
export const adminGrantEmployerL3TrialBodySchema = z.object({
    trialEndsAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, 'trialEndsAt must be YYYY-MM-DD hh:mm'),
});
/** Admin archive + anonymize an app user (keeps business data via ended pairing). */
export const adminRemoveAppUserBodySchema = z.object({
    adminPassword: z.string().min(1, 'Admin password is required'),
    reason: z.string().trim().min(1).max(500),
});
// ── Update System Settings Input Schema ──
/** Upgrade copy line: non-empty text only; placeholders (e.g. {{amount}}) are optional. */
const upgradeCopyLineSchema = (maxLen) => z.string().trim().min(1).max(maxLen);
const subscriptionUpgradeCopyLocaleSchema = z.object({
    payTodayTitle: z
        .string()
        .trim()
        .min(1)
        .max(SUBSCRIPTION_UPGRADE_COPY_MAX_TITLE_LENGTH),
    payTodayAmount: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    promoFirstMonthPrice: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    promoLimitedTimePrice: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    promoFirstMonthBadge: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    promoLimitedTimeBadge: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    deferredTitle: z
        .string()
        .trim()
        .min(1)
        .max(SUBSCRIPTION_UPGRADE_COPY_MAX_TITLE_LENGTH),
    deferredNoChargeToday: z
        .string()
        .trim()
        .min(1)
        .max(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    deferredFirstChargeOn: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    deferredCoversPeriod: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
    deferredInvitationTrialNote: upgradeCopyLineSchema(SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH),
});
export const subscriptionUpgradeCopySettingSchema = z
    .object({
    defaultLocale: z
        .string()
        .trim()
        .regex(SUBSCRIPTION_UPGRADE_COPY_LOCALE_CODE_REGEX, 'Invalid defaultLocale'),
    locales: z
        .record(z.string(), subscriptionUpgradeCopyLocaleSchema)
        .refine((locales) => Object.keys(locales).length >= 1, 'At least one locale is required')
        .refine((locales) => Object.keys(locales).length <= SUBSCRIPTION_UPGRADE_COPY_MAX_LOCALES, `At most ${SUBSCRIPTION_UPGRADE_COPY_MAX_LOCALES} locales are allowed`)
        .refine((locales) => Object.keys(locales).every((key) => SUBSCRIPTION_UPGRADE_COPY_LOCALE_CODE_REGEX.test(key.trim())), 'Invalid locale code in locales map'),
})
    .refine((value) => value.locales[value.defaultLocale] != null, {
    message: 'defaultLocale must exist in locales',
    path: ['defaultLocale'],
});
const nullableNonNegIntSchema = z.number().int().min(0).nullable();
const taskKindSchema = z.enum([
    TASK_KIND.STANDARD,
    TASK_KIND.INVENTORY,
    TASK_KIND.PICKUP_DROPOFF,
    TASK_KIND.MEDICATION_REMINDER,
]);
const employerLevelLimitsConfigSchema = z.object({
    maxTotalTasks: nullableNonNegIntSchema,
    maxDailyTaskSubmits: positiveIntSchema.max(1000),
    maxDailyTaskReviews: positiveIntSchema.max(1000),
    maxDailyReceipts: positiveIntSchema.max(1000),
    maxDailyAiAnalyze: positiveIntSchema.max(1000),
    maxCommentsPerTarget: positiveIntSchema.max(500),
    maxCommentLength: positiveIntSchema.max(1000),
    autoTranslationEnabled: z.boolean(),
    allowedTaskKinds: z.array(taskKindSchema).min(1),
    maxInventoryLocations: nullableNonNegIntSchema,
    maxInventoryCategories: nullableNonNegIntSchema,
});
const limitsByLevelAdminMapSchema = z.object({
    '1': employerLevelLimitsConfigSchema,
    '2': employerLevelLimitsConfigSchema,
    '3': employerLevelLimitsConfigSchema,
});
const photoRetentionDaysByLevelSchema = z.object({
    '1': positiveIntSchema.max(365),
    '2': positiveIntSchema.max(365),
    '3': positiveIntSchema.max(365),
});
const pinMessageLimitsSchema = z.object({
    maxItems: positiveIntSchema.max(200),
    titleMaxLength: positiveIntSchema.max(500),
    contentMaxLength: positiveIntSchema.max(5000),
});
const pinMessageLimitsByLevelSchema = z.object({
    '1': pinMessageLimitsSchema,
    '2': pinMessageLimitsSchema,
    '3': pinMessageLimitsSchema,
});
const subscriptionPromoDatetimeSchema = z
    .string()
    .refine((value) => isSubscriptionPromoDatetime(value), {
    message: 'Must be YYYY-MM-DD hh:mm (UTC)',
});
const limitedTimePromoOfferSchema = z.object({
    id: z.string().min(1).max(80),
    enabled: z.boolean(),
    billingInterval: z.enum(['month', 'year']),
    amountOff: z.number().int().min(0).max(1_000_000),
    stripeCouponId: z.string().max(200),
    startsAt: subscriptionPromoDatetimeSchema,
    endsAt: subscriptionPromoDatetimeSchema,
    adminNote: z.string().max(200).optional(),
});
const subscriptionPromotionsSettingSchema = z
    .object({
    newUserFirstMonth: z.object({
        enabled: z.boolean(),
        amountOff: z.number().int().min(0).max(1_000_000),
        stripeCouponId: z.string().max(200),
    }),
    limitedTimeOffers: z.array(limitedTimePromoOfferSchema).max(40),
})
    .superRefine((value, ctx) => {
    if (value.newUserFirstMonth.enabled && value.newUserFirstMonth.stripeCouponId.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'stripeCouponId is required when new-user promo is enabled',
            path: ['newUserFirstMonth', 'stripeCouponId'],
        });
    }
    if (value.newUserFirstMonth.enabled && value.newUserFirstMonth.amountOff < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'amountOff must be >= 1 when enabled',
            path: ['newUserFirstMonth', 'amountOff'],
        });
    }
    const seenIds = new Set();
    value.limitedTimeOffers.forEach((offer, index) => {
        if (seenIds.has(offer.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Offer id must be unique',
                path: ['limitedTimeOffers', index, 'id'],
            });
        }
        seenIds.add(offer.id);
        if (offer.enabled && offer.stripeCouponId.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'stripeCouponId is required when limited-time offer is enabled',
                path: ['limitedTimeOffers', index, 'stripeCouponId'],
            });
        }
        if (offer.enabled && offer.amountOff < 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'amountOff must be >= 1 when enabled',
                path: ['limitedTimeOffers', index, 'amountOff'],
            });
        }
        if (offer.endsAt < offer.startsAt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'endsAt must be >= startsAt',
                path: ['limitedTimeOffers', index, 'endsAt'],
            });
        }
    });
});
export const updateSystemSettingsInputSchema = z.object({
    maxCommentsPerTarget: positiveIntSchema.optional(),
    maxCommentLength: positiveIntSchema.max(1000).optional(),
    maxDailyTasks: positiveIntSchema.max(100).optional(),
    maxDailyReceipts: positiveIntSchema.max(100).optional(),
    maxDailyAIAnalyze: positiveIntSchema.max(1000).optional(),
    photoRetentionDays: positiveIntSchema.max(365).optional(),
    photoRetentionDaysByLevel: photoRetentionDaysByLevelSchema.optional(),
    currentConsentVersion: z.string().min(1).optional(),
    auditLogRetentionYears: positiveIntSchema.max(10).optional(),
    pinMessageMaxItems: positiveIntSchema.max(200).optional(),
    pinMessageTitleMaxLength: positiveIntSchema.max(500).optional(),
    pinMessageContentMaxLength: positiveIntSchema.max(5000).optional(),
    pinMessageLimitsByLevel: pinMessageLimitsByLevelSchema.optional(),
    invitationCodeRequired: z.boolean().optional(),
    playStoreShowOfficialWebsiteUrl: z.boolean().optional(),
    subscriptionUpgradeCopy: subscriptionUpgradeCopySettingSchema.optional(),
    subscriptionPromotions: subscriptionPromotionsSettingSchema.optional(),
    membershipPricingCatalog: membershipPricingCatalogSettingSchema.optional(),
    limitsByLevel: limitsByLevelAdminMapSchema.optional(),
    trialDaysNew: positiveIntSchema.max(365).optional(),
    trialDaysMigration: positiveIntSchema.max(365).optional(),
    featureLaunchAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, 'Must be YYYY-MM-DD hh:mm (UTC)')
        .optional(),
});
// ── Audit Log Query Schema ──
export const auditLogQuerySchema = z.object({
    userEmail: z.string().trim().min(1).max(320).optional(),
    action: z.string().optional(),
    targetType: z.string().optional(),
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
//# sourceMappingURL=admin.schema.js.map